import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from '@rd-web-markets/shared/dist/util/buttons';
import { Sidebar as PSidebar, Menu, SubMenu, MenuItem } from 'react-pro-sidebar';
import { useTranslation } from 'react-i18next';

/**
 * @param {Array} headerItem - { text: String, link: String, icon: String }
 * @param {Array} menuItems - { text: String, highlighted: boolean, link: String, subMenu: Array }
 * @param Boolean shouldReload - if true, will reload page on link click
 * @param Int dividerPosition - add divider line to particular place in menu
 * @param Component children - add child component to sidebar top part
 * @param Function renderCustomItems - custom function to render special menu items
 *
 * React Portal with sidebar component. Displays list of links and a header item which is usually a link to previous page
 *
 */
const SidebarPortal = _ref => {
  let {
    headerItem,
    menuItems,
    dividerPosition,
    shouldReload,
    children,
    renderCustomItems
  } = _ref;
  const [collapsed, setCollapsed] = useState(false);
  const user = useSelector(state => state.auth.user);
  const {
    t
  } = useTranslation();
  const container = document.getElementById('sidebar-container');
  const contentWrapper = document.getElementById('content-wrapper');
  const forceUpdate = link => {
    if (shouldReload) {
      window.location.assign(window.location.origin + link);
    }
  };
  const handleSidebarCollapse = () => {
    setCollapsed(prev => !prev);
    contentWrapper.style.left = collapsed ? '240px' : '80px';
  };
  const hanldeClosedSubMenuClick = () => {
    if (collapsed) {
      setCollapsed(false);
      contentWrapper.style.left = '240px';
    }
  };
  const renderMenu = items => {
    return items.map((item, index) => {
      if (item.isClaimGroupSelector && renderCustomItems) {
        return renderCustomItems([item])[0];
      }
      if (item.subMenu) {
        return /*#__PURE__*/React.createElement(React.Fragment, {
          key: "menu-".concat(index)
        }, /*#__PURE__*/React.createElement(SubMenu, {
          icon: item.icon && /*#__PURE__*/React.createElement("span", {
            className: "material-icons"
          }, item.icon),
          onClick: hanldeClosedSubMenuClick,
          key: index,
          label: t(item.text),
          defaultOpen: item.subMenu.some(subMenuItem => subMenuItem.highlighted) || item.isOpen
        }, item.subMenu.map((subItem, subIndex) => {
          return /*#__PURE__*/React.createElement(MenuItem, {
            key: "submenu-".concat(index, "-").concat(subIndex),
            className: "",
            component: /*#__PURE__*/React.createElement(Link, {
              onClick: () => forceUpdate("/".concat(user.account_type, "/").concat(subItem.link)),
              to: "/".concat(user.account_type, "/").concat(subItem.link),
              title: t(subItem.text)
            })
          }, subItem.highlighted ? /*#__PURE__*/React.createElement("b", null, t(subItem.text)) : t(subItem.text));
        })), index === dividerPosition && /*#__PURE__*/React.createElement("hr", {
          className: "mx-3"
        }));
      } else if (item.isTitle) {
        return /*#__PURE__*/React.createElement("div", {
          key: index,
          className: "text-secondary text-uppercase ps-3 mt-4 ms-1"
        }, t(item.text));
      }
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuItem, {
        key: index,
        icon: item.icon && /*#__PURE__*/React.createElement("span", {
          className: "material-icons"
        }, item.icon),
        component: /*#__PURE__*/React.createElement(Link, {
          onClick: () => forceUpdate("/".concat(user.account_type, "/").concat(item.link)),
          to: "/".concat(user.account_type, "/").concat(item.link),
          title: t(item.text)
        })
      }, item.highlighted ? /*#__PURE__*/React.createElement("b", null, t(item.text)) : t(item.text)), index === dividerPosition && /*#__PURE__*/React.createElement("hr", {
        className: "mx-3"
      }));
    });
  };
  useEffect(() => {
    if (contentWrapper) {
      contentWrapper.style.left = '240px';
    }
    return () => {
      if (contentWrapper) {
        contentWrapper.style.left = '0px';
      }
    };
  }, [contentWrapper]);
  const SidebarComponent = /*#__PURE__*/React.createElement(PSidebar, {
    collapsed: collapsed,
    backgroundColor: "#ffffff",
    width: "240px",
    collapsedWidth: "80px",
    rootStyles: {
      position: 'fixed',
      height: '100%',
      fontSize: '1rem'
    }
  }, /*#__PURE__*/React.createElement(Menu, {
    rootStyles: {
      background: '#0078B9 linear-gradient(180deg, #268cc4, #0078B9) repeat-x !important',
      height: '128px',
      paddingTop: '70px',
      color: '#ffffff'
    }
  }, headerItem && /*#__PURE__*/React.createElement(MenuItem, {
    component: /*#__PURE__*/React.createElement(Link, {
      to: "/".concat(user.account_type).concat(headerItem.link ? '/' + headerItem.link : ''),
      title: t(headerItem.text)
    })
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons me-2"
  }, headerItem.icon || 'arrow_back'), t(headerItem.text))), children, /*#__PURE__*/React.createElement(Menu, {
    rootStyles: {
      color: '#0078B9'
    }
  }, menuItems && renderMenu(menuItems)), /*#__PURE__*/React.createElement(Menu, {
    rootStyles: {
      position: 'absolute',
      bottom: '1em'
    }
  }, /*#__PURE__*/React.createElement(MenuItem, null, /*#__PURE__*/React.createElement(Button, {
    onClick: () => handleSidebarCollapse(),
    variant: "outline-primary",
    icon: collapsed ? 'arrow_right' : 'arrow_left'
  }))));
  const portal = container ? /*#__PURE__*/ReactDOM.createPortal(SidebarComponent, container) : '';
  return /*#__PURE__*/React.createElement(React.Fragment, null, portal);
};
export default SidebarPortal;